'use client'

/**
 * Auth Context Provider
 *
 * Provides authentication state throughout the app with React Context.
 * Handles session management, OAuth login, and subscription status.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react'

// localStorage key for skeleton loading hint
const AUTH_HINT_KEY = 'auth_skeleton_hint'
import {
  OAUTH_PENDING_REDIRECT_KEY,
  OAUTH_PENDING_REDIRECT_MAX_ATTEMPTS,
  OAUTH_PENDING_REDIRECT_TTL_MS,
  markOAuthRedirectReplacing,
  type PendingOAuthRedirect,
} from '@/lib/auth/oauth-pending-redirect'
const SUBSCRIPTION_CACHE_TTL = 5 * 60 * 1000 // 5 minutes
type OAuthRedirectRecoverySource = 'auth_init' | 'signed_in'
// Keep the provider-facing callback fallback compact and free of uploaded-image
// URLs, but preserve enough of an analysis continuation to return to the exact
// tool and join the quota-resume funnel even when localStorage is unavailable.
const OAUTH_CALLBACK_NEXT_PARAMS = [
  'zipCode',
  'grassType',
  'season',
  'analysisId',
  'access',
  'analysis_id',
  'funnel_id',
  'lawnId',
  'resumeAnalysis',
  'analysisResumeId',
  'scanRecovery',
  'mode',
] as const

function normalizeRedirectPath(redirectTo?: string): string | null {
  if (!redirectTo || typeof window === 'undefined') return null

  try {
    const parsed = new URL(redirectTo, window.location.origin)
    if (parsed.origin !== window.location.origin) return null
    return `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return null
  }
}

function storePendingOAuthRedirect(path: string | null) {
  if (!path || typeof window === 'undefined') return

  try {
    localStorage.setItem(
      OAUTH_PENDING_REDIRECT_KEY,
      JSON.stringify({ path, savedAt: Date.now() })
    )
  } catch {
    // Ignore - localStorage may be blocked
  }
}

function buildOAuthCallbackNextPath(path: string | null): string | null {
  if (!path || typeof window === 'undefined') return null

  try {
    const parsed = new URL(path, window.location.origin)
    if (parsed.origin !== window.location.origin) return null

    const params = new URLSearchParams()
    for (const key of OAUTH_CALLBACK_NEXT_PARAMS) {
      const value = parsed.searchParams.get(key)
      if (value) params.set(key, value)
    }

    const query = params.toString()
    return `${parsed.pathname}${query ? `?${query}` : ''}`
  } catch {
    return null
  }
}

/**
 * Reads the stored resume path. The key is NOT dropped on read: the replace
 * that follows can lose the navigation to a chunk-error reload fired in the
 * same tick, and a key deleted before the replace committed left that reload
 * with nothing to recover (see lib/auth/oauth-pending-redirect.ts). It is
 * dropped when it is invalid or expired, when this document already IS the
 * destination, when the destination consumes it, or after the attempt cap.
 */
function consumePendingOAuthRedirect(): PendingOAuthRedirect | null {
  if (typeof window === 'undefined') return null

  const drop = () => {
    try {
      localStorage.removeItem(OAUTH_PENDING_REDIRECT_KEY)
    } catch {
      // Ignore - localStorage may be blocked
    }
  }

  try {
    const raw = localStorage.getItem(OAUTH_PENDING_REDIRECT_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as { path?: unknown; savedAt?: unknown; attempts?: unknown }
    if (typeof parsed.path !== 'string' || typeof parsed.savedAt !== 'number') {
      drop()
      return null
    }
    if (Date.now() - parsed.savedAt > OAUTH_PENDING_REDIRECT_TTL_MS) {
      drop()
      return null
    }

    const pendingPath = normalizeRedirectPath(parsed.path)
    if (!pendingPath) {
      drop()
      return null
    }

    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
    const attempts = typeof parsed.attempts === 'number' ? parsed.attempts : 0
    if (currentPath === pendingPath || attempts + 1 >= OAUTH_PENDING_REDIRECT_MAX_ATTEMPTS) {
      drop()
    } else {
      try {
        localStorage.setItem(
          OAUTH_PENDING_REDIRECT_KEY,
          JSON.stringify({ path: parsed.path, savedAt: parsed.savedAt, attempts: attempts + 1 }),
        )
      } catch {
        // Ignore - localStorage may be blocked
      }
    }
    return { path: pendingPath, savedAt: parsed.savedAt, attempts }
  } catch {
    drop()
    return null
  }
}

/**
 * Save auth state hint to localStorage for smoother skeleton loading
 * This allows LoginButton to show the appropriate skeleton shape before auth initializes
 */
function saveAuthHint(isLoggedIn: boolean) {
  try {
    if (typeof window !== 'undefined') {
      localStorage.setItem(AUTH_HINT_KEY, isLoggedIn ? 'logged_in' : 'logged_out')
    }
  } catch {
    // Ignore - localStorage may be blocked in private browsing
  }
}

/**
 * Read auth state hint from localStorage (exported for LoginButton to use)
 */
export function getAuthHint(): 'logged_in' | 'logged_out' | null {
  try {
    if (typeof window !== 'undefined') {
      const hint = localStorage.getItem(AUTH_HINT_KEY)
      if (hint === 'logged_in' || hint === 'logged_out') {
        return hint
      }
    }
  } catch {
    // Ignore - localStorage may be blocked
  }
  return null
}
import posthog from 'posthog-js'
import { createClient } from '@/lib/auth/supabase-auth'
import type { User, Session } from '@supabase/supabase-js'
import type { SubscriptionInfo } from '@/lib/auth/subscription'
import {
  analytics,
  firstTouchSetOnce,
  stashPosthogIdentityForOAuth,
  takeEmailCaptureAttribution,
  trackEvent,
} from '@/lib/analytics'
import { rememberLastProvider } from '@/lib/auth/last-provider'
import { justAuthedCookieFresh } from '@/lib/claim-analysis'
import { setPremiumAdHint } from '@/lib/ads/journey-tag'

interface AuthContextType {
  // Auth state
  user: User | null
  session: Session | null
  loading: boolean
  isLoading: boolean // Alias for loading
  initialized: boolean

  // Subscription info
  subscription: SubscriptionInfo | null
  isPremium: boolean
  subscriptionLoading: boolean
  /** True when the last subscription lookup failed. `isPremium: false` is then
      an ABSENCE of an answer, not a negative one — a paid surface must treat it
      as unknown and fail closed rather than sell to an existing subscriber. */
  subscriptionError: boolean

  // Auth actions
  signInWithGoogle: (redirectTo?: string, navigationTarget?: Window | null) => Promise<void>
  signInWithApple: (redirectTo?: string, navigationTarget?: Window | null) => Promise<void>
  signOut: () => Promise<void>

  // Refresh subscription
  refreshSubscription: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null)

  /**
   * Single write path for subscription state. Every setter goes through here so
   * the ad-free hint can never drift from the tier — including the null cases,
   * where a stale hint would keep a lapsed or signed-out account ad-free.
   */
  const applySubscription = useCallback((next: SubscriptionInfo | null) => {
    setSubscription(next)
    setPremiumAdHint(next?.isPremium === true)
  }, [])
  const [loading, setLoading] = useState(true)
  const [initialized, setInitialized] = useState(false)
  // Subscription readiness is tracked separately from auth readiness. `loading`
  // / `initialized` answer "do we know who this is"; this answers "do we know
  // what they pay for". Surfaces that render a free-tier or upsell state must
  // wait on this one, but nothing that only needs identity should.
  const [subscriptionLoading, setSubscriptionLoading] = useState(true)
  const [subscriptionError, setSubscriptionError] = useState(false)

  const supabase = createClient()

  // Subscription cache to prevent redundant API calls
  const subscriptionCacheRef = useRef<{
    data: SubscriptionInfo | null
    timestamp: number
    userId: string
  } | null>(null)

  // Track the previous session so we can distinguish a fresh login (null →
  // session) from a token refresh (session → session). supabase-js emits
  // `SIGNED_IN` for both, but only the former should drop the `justAuthed`
  // sentinel that triggers `pendingAnalysis_flow` rehydration on the homepage.
  const previousSessionRef = useRef<Session | null>(null)

  // Ref mirror of `initialized`, because the auth-change handler closes over
  // state from its first render and must read the live value. supabase-js's
  // own `initialize()` → `_recoverAndRefresh()` emits `SIGNED_IN` on every hard
  // page load of an already-signed-in browser, and it can land before
  // `initializeAuth` has populated `previousSessionRef` — so on that path a
  // page load is indistinguishable from a fresh login by session alone.
  const initializedRef = useRef(false)

  // Bumped whenever the answer to "what does this user pay for" is invalidated:
  // a new lookup starts, the cache is cleared, or the user signs out. A lookup
  // that resolves under a stale generation is discarded so a late response
  // can't re-apply one session's entitlement to the next.
  const subscriptionGenRef = useRef(0)

  const resumePendingOAuthRedirect = useCallback((source: OAuthRedirectRecoverySource) => {
    const pendingRedirect = consumePendingOAuthRedirect()
    if (!pendingRedirect || typeof window === 'undefined') return

    const pendingPath = pendingRedirect.path

    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
    if (currentPath === pendingPath) return

    let fromPathname = window.location.pathname
    let toPathname = pendingPath
    try {
      fromPathname = new URL(currentPath, window.location.origin).pathname
      toPathname = new URL(pendingPath, window.location.origin).pathname
    } catch {
      // Keep fallback values when URL parsing fails.
    }

    trackEvent('oauth_redirect_recovered', {
      source,
      from_path: fromPathname,
      to_path: toPathname,
      had_query: pendingPath.includes('?'),
      had_hash: pendingPath.includes('#'),
      elapsed_ms: Math.max(0, Date.now() - pendingRedirect.savedAt),
      path_length: pendingPath.length,
    })

    // The replace tears this document down asynchronously. Anything the page
    // starts after this point (the analysis hook's post-auth quota auto-run and
    // its fetch) dies mid-flight and reports as a failure that never happened.
    // Flag it before we commit so those callers can stand down.
    markOAuthRedirectReplacing()
    window.location.replace(pendingPath)
  }, [])

  // Fetch subscription info for a user
  const fetchSubscription = useCallback(async (userId: string) => {
    const now = Date.now()
    const cache = subscriptionCacheRef.current

    // Check cache first - return cached data if still valid
    if (cache && cache.userId === userId && now - cache.timestamp < SUBSCRIPTION_CACHE_TTL) {
      applySubscription(cache.data)
      return
    }

    // Cache miss or expired - fetch from API
    const generation = subscriptionGenRef.current
    try {
      const response = await fetch(`/api/user/subscription?userId=${userId}`)
      if (generation !== subscriptionGenRef.current) return
      if (!response.ok) {
        if (generation === subscriptionGenRef.current) setSubscriptionError(true)
      }
      if (response.ok) {
        const data = await response.json()
        if (generation !== subscriptionGenRef.current) return
        setSubscriptionError(false)
        applySubscription(data)
        // Update cache with fresh data
        subscriptionCacheRef.current = { data, timestamp: now, userId }

        // Update PostHog with subscription properties for segmentation
        if (typeof window !== 'undefined' && data) {
          posthog.people.set({
            subscription_tier: data.isPremium ? 'premium' : 'free',
            subscription_status: data.status,
          })
        }
      }
    } catch (error) {
      console.error('Error fetching subscription:', error)
      if (generation === subscriptionGenRef.current) setSubscriptionError(true)
    }
  }, [applySubscription])

  /**
   * Subscription load that also drives `subscriptionLoading`. Only used on the
   * paths where the answer is not yet known (initial session, sign-in), so an
   * explicit `refreshSubscription` never flips consumers back into a loading
   * state over content they are already showing.
   */
  const loadSubscription = useCallback(async (userId: string) => {
    const generation = ++subscriptionGenRef.current
    const cache = subscriptionCacheRef.current
    const cacheIsFresh =
      cache?.userId === userId && Date.now() - cache.timestamp < SUBSCRIPTION_CACHE_TTL
    if (!cacheIsFresh) setSubscriptionLoading(true)

    try {
      await fetchSubscription(userId)
    } finally {
      // Only the newest lookup may declare the subscription resolved.
      if (generation === subscriptionGenRef.current) setSubscriptionLoading(false)
    }
  }, [fetchSubscription])

  // Refresh subscription status (clears cache to force fresh fetch)
  const refreshSubscription = useCallback(async () => {
    subscriptionCacheRef.current = null
    subscriptionGenRef.current += 1
    if (user?.id) {
      await fetchSubscription(user.id)
    }
  }, [user?.id, fetchSubscription])

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Get initial session
        const { data: { session: initialSession } } = await supabase.auth.getSession()

        if (initialSession) {
          previousSessionRef.current = initialSession
          setSession(initialSession)
          setUser(initialSession.user)
          saveAuthHint(true) // Save hint for skeleton loading

          // Auth readiness must not wait on the subscription lookup. While this
          // was awaited, a slow /api/user/subscription response held `loading`
          // true, and every `!user`-gated surface rendered its signed-out state
          // to a signed-in (including paying) visitor. Subscription readiness
          // has its own flag for the surfaces that actually need it.
          void loadSubscription(initialSession.user.id)

          // Identify user in PostHog on initial load
          if (typeof window !== 'undefined') {
            posthog.identify(initialSession.user.id, {
              email: initialSession.user.email,
              // $email additively alongside the legacy lowercase key: PostHog's
              // built-in cohorts/display-name machinery only reads $email
              // (2026-08-28 tracking audit P0-5). Never drop the legacy key.
              $email: initialSession.user.email,
              name: initialSession.user.user_metadata?.full_name || initialSession.user.user_metadata?.name,
              avatar_url: initialSession.user.user_metadata?.avatar_url,
              auth_provider: initialSession.user.app_metadata?.provider,
              created_at: initialSession.user.created_at,
            }, firstTouchSetOnce())
          }

          // OAuth providers occasionally drop query params on callback redirects.
          // This recovers the intended destination when we have a pending redirect.
          resumePendingOAuthRedirect('auth_init')
        } else {
          // No session - user is not logged in
          saveAuthHint(false)
          setSubscriptionError(false)
          setSubscriptionLoading(false)
        }
      } catch (error) {
        console.error('Error initializing auth:', error)
        setSubscriptionLoading(false)
      } finally {
        // Set in the `finally`, not on session receipt: the boot `SIGNED_IN`
        // can arrive either side of `getSession()` resolving, and only "the
        // bootstrap is done" makes a later SIGNED_IN a real login.
        initializedRef.current = true
        setLoading(false)
        setInitialized(true)
      }
    }

    initializeAuth()

    // Subscribe to auth changes
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        const prevSession = previousSessionRef.current
        previousSessionRef.current = newSession
        setSession(newSession)
        setUser(newSession?.user ?? null)

        // supabase-js emits `SIGNED_IN` for token refreshes as well as real
        // logins. Everything that should happen once per login — the
        // `justAuthed` sentinel, the last-provider hint, the login/signup
        // analytics, and the pending-OAuth redirect recovery — is gated on this
        // true null → session transition. A refresh must be a no-op.
        const isFreshLogin = prevSession === null

        if (newSession?.user) {
          saveAuthHint(true) // Save hint for skeleton loading
          void loadSubscription(newSession.user.id)

          // Identify user in PostHog for analytics
          // This links anonymous pre-auth activity to their authenticated profile
          if (typeof window !== 'undefined') {
            posthog.identify(newSession.user.id, {
              email: newSession.user.email,
              $email: newSession.user.email,
              name: newSession.user.user_metadata?.full_name || newSession.user.user_metadata?.name,
              avatar_url: newSession.user.user_metadata?.avatar_url,
              auth_provider: newSession.user.app_metadata?.provider,
              created_at: newSession.user.created_at,
            }, firstTouchSetOnce())
          }

          // Track signup vs login based on event type and account age. Gated on
          // `isFreshLogin` so a token refresh or cross-tab broadcast emits
          // nothing and cannot trigger the `window.location.replace` below.
          if (event === 'SIGNED_IN' && isFreshLogin) {
            // Drop a short-lived sentinel so the homepage hook can tell a
            // genuine fresh sign-in from a normal page refresh, and only
            // rehydrate `pendingAnalysis_flow` in the former case.
            try {
              if (typeof window !== 'undefined') {
                window.sessionStorage.setItem('justAuthed', String(Date.now()))
                window.sessionStorage.removeItem('justAuthedConsumed')
              }
            } catch { /* sessionStorage unavailable */ }

            // Remember the provider so the sign-in screen can show a
            // "Last used" hint next time (recognition beats recall).
            rememberLastProvider(newSession.user.app_metadata?.provider)

            // supabase-js re-emits SIGNED_IN on every hard page load of an
            // already-signed-in browser (initialize() → _recoverAndRefresh()),
            // and that can beat initializeAuth's getSession(), so the session
            // check above cannot tell it from a real login. Requiring the
            // bootstrap to be finished can. Genuine OAuth logins are still
            // counted: app/auth/callback/route.ts emits login_completed and
            // signup_completed server-side after the code exchange.
            if (initializedRef.current) {
              const provider = newSession.user.app_metadata?.provider || 'email'
              const createdAt = new Date(newSession.user.created_at).getTime()
              const now = Date.now()
              const isNewUser = now - createdAt < 60000 // Created within last 60 seconds
              let pendingEmailCapture: ReturnType<typeof takeEmailCaptureAttribution> = null
              try {
                pendingEmailCapture = takeEmailCaptureAttribution()
              } catch {
                // localStorage / JSON parse failures are non-fatal
              }

              // The OAuth callback route fires signup_completed server-side
              // (with capture_source attribution) whenever the code exchange
              // happened there — recognizable by a fresh just_authed cookie.
              // Skip the client capture in that case to avoid double-counting.
              // Email/password signups never pass through the callback, so they
              // still fire here.
              const serverHandledOAuth = provider !== 'email' && justAuthedCookieFresh(180_000)

              if (isNewUser) {
                // The guard suppresses ONLY the duplicate signup_completed.
                // emailCaptured is a separate stream (email-capture funnels)
                // with no server-side counterpart, so it always fires for new
                // users with a pending stash — including when the cookie write
                // failed and the callback emitted an unattributed signup.
                if (!serverHandledOAuth) {
                  trackEvent('signup_completed', {
                    provider,
                    platform: 'web',
                    email_domain: newSession.user.email?.split('@')[1],
                  })
                }
                if (pendingEmailCapture) {
                  analytics.emailCaptured(pendingEmailCapture.source, undefined, {
                    captureType: 'oauth_signup',
                  })
                }
              } else {
                trackEvent('login_completed', {
                  provider,
                  platform: 'web',
                })
              }

              // Attribute conversion to a recent AccountPromptModal show, if any.
              // Modal stashes { variant, grassType, ts } in localStorage on show;
              // we attribute within 5 minutes of that show.
              try {
                if (typeof window !== 'undefined') {
                  const raw = window.localStorage.getItem('account_prompt_pending_attribution')
                  if (raw) {
                    const stash = JSON.parse(raw) as { variant?: string; grassType?: string; ts?: number }
                    if (stash?.ts && Date.now() - stash.ts < 5 * 60 * 1000) {
                      trackEvent('account_prompt_succeeded', {
                        variant: stash.variant,
                        grassType: stash.grassType,
                        provider,
                        isNewUser,
                      })
                    }
                    window.localStorage.removeItem('account_prompt_pending_attribution')
                  }
                }
              } catch {
                // localStorage / JSON parse failures are non-fatal
              }

              resumePendingOAuthRedirect('signed_in')
            }
          }
        } else {
          subscriptionGenRef.current += 1
          applySubscription(null)
          setSubscriptionError(false)
          setSubscriptionLoading(false)
        }

        if (event === 'SIGNED_OUT') {
          saveAuthHint(false) // Save hint for skeleton loading
          applySubscription(null)
          trackEvent('logout_completed')
          // Reset PostHog identity on logout to prevent cross-user data pollution
          if (typeof window !== 'undefined') {
            posthog.reset()
          }
        }
      }
    )

    return () => {
      authSubscription.unsubscribe()
    }
  }, [supabase.auth, loadSubscription, resumePendingOAuthRedirect, applySubscription])

  // Sign in with Google
  // Always routes through /auth/callback so Stripe customer creation
  // and Discord notifications happen for new users
  const signInWithGoogle = useCallback(async (redirectTo?: string, navigationTarget?: Window | null) => {
    const normalizedRedirect = normalizeRedirectPath(redirectTo)
    storePendingOAuthRedirect(normalizedRedirect)

    const callbackUrl = new URL('/auth/callback', window.location.origin)
    // Supabase dashboard allowlist must cover apex, www, and Vercel preview
    // /auth/callback URLs. This code only keeps the `next` fallback short.
    const callbackNext = buildOAuthCallbackNextPath(normalizedRedirect)
    if (callbackNext) callbackUrl.searchParams.set('next', callbackNext)
    stashPosthogIdentityForOAuth()

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: callbackUrl.toString(),
        queryParams: {
          prompt: 'select_account',
        },
        skipBrowserRedirect: Boolean(navigationTarget),
      },
    })

    if (error) {
      console.error('Google sign in error:', error)
      throw error
    }
    if (navigationTarget) {
      if (!data?.url) {
        navigationTarget.close()
        throw new Error('Google sign in did not return a redirect URL')
      }
      navigationTarget.location.replace(data.url)
    }
  }, [supabase.auth])

  // Sign in with Apple
  const signInWithApple = useCallback(async (redirectTo?: string, navigationTarget?: Window | null) => {
    const normalizedRedirect = normalizeRedirectPath(redirectTo)
    storePendingOAuthRedirect(normalizedRedirect)

    const callbackUrl = new URL('/auth/callback', window.location.origin)
    // Supabase dashboard allowlist must cover apex, www, and Vercel preview
    // /auth/callback URLs. This code only keeps the `next` fallback short.
    const callbackNext = buildOAuthCallbackNextPath(normalizedRedirect)
    if (callbackNext) callbackUrl.searchParams.set('next', callbackNext)
    stashPosthogIdentityForOAuth()

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        redirectTo: callbackUrl.toString(),
        skipBrowserRedirect: Boolean(navigationTarget),
      },
    })

    if (error) {
      console.error('Apple sign in error:', error)
      throw error
    }
    if (navigationTarget) {
      if (!data?.url) {
        navigationTarget.close()
        throw new Error('Apple sign in did not return a redirect URL')
      }
      navigationTarget.location.replace(data.url)
    }
  }, [supabase.auth])

  // Sign out
  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut()
    if (error) {
      console.error('Sign out error:', error)
      throw error
    }
    setUser(null)
    setSession(null)
    subscriptionGenRef.current += 1
    setSubscription(null)
    setSubscriptionLoading(false)
    previousSessionRef.current = null
    // Clear subscription cache on sign out
    subscriptionCacheRef.current = null
  }, [supabase.auth])

  const value: AuthContextType = {
    user,
    session,
    loading,
    isLoading: loading,
    initialized,
    subscription,
    isPremium: subscription?.isPremium ?? false,
    subscriptionLoading,
    subscriptionError,
    signInWithGoogle,
    signInWithApple,
    signOut,
    refreshSubscription,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Hook to access auth context
 */
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

/**
 * Hook that returns only when auth is initialized
 * Useful for components that need to wait for auth state
 */
export function useAuthInitialized() {
  const { initialized, loading, ...rest } = useAuth()
  return { initialized, loading, isReady: initialized && !loading, ...rest }
}
